<template>
    <b-modal
        v-model="isModalOpen"
        title="고객 관리"
        size="xl"
        modal-class="customer-management-modal"
        header-class="customer-modal-header"
        body-class="customer-modal-body"
        footer-class="customer-modal-footer"
        hide-footer
        @hidden="handleClose"
    >
        <div class="modal-content">
            <!-- 등록된 고객 섹션 -->
            <div class="section section-left">
                <div class="section-header">
                    <div class="section-title-row">
                        <h4 class="section-title">등록된 고객</h4>
                        <div class="status-summary">
                            <span class="status-count active">{{ activeCustomerCount }}</span>
                            <span class="status-separator">/</span>
                            <span class="status-count total">{{ registeredCustomers.length }}</span>
                        </div>
                    </div>
                    <div class="search-box">
                        <i class="material-icons search-icon">search</i>
                        <input
                            v-model="searchRegistered"
                            type="text"
                            class="search-input"
                            placeholder="고객명 또는 고객번호 검색..."
                            @input="onSearchRegistered"
                        />
                        <i v-if="searchRegistered" class="material-icons clear-icon" @click="clearSearchRegistered">
                            close
                        </i>
                    </div>
                </div>
                <div class="customer-list-container">
                    <div v-if="isLoadingRegistered" class="loading-state">
                        <i class="material-icons spinner-icon">sync</i>
                        <span>로딩 중...</span>
                    </div>
                    <div v-else-if="registeredCustomers.length === 0" class="empty-state">
                        <i class="material-icons empty-icon">people_outline</i>
                        <p class="empty-text">등록된 고객이 없습니다</p>
                        <p class="empty-subtext">우측에서 고객을 추가해보세요</p>
                    </div>
                    <div v-else-if="filteredRegisteredCustomers.length === 0" class="empty-state">
                        <i class="material-icons empty-icon">search_off</i>
                        <p class="empty-text">검색 결과가 없습니다</p>
                    </div>
                    <div v-else class="customer-list">
                        <div
                            v-for="customer in filteredRegisteredCustomers"
                            :key="customer.custNo"
                            class="customer-item"
                            :class="{ inactive: customer.status !== 'active' }"
                        >
                            <div class="customer-main">
                                <div class="customer-info">
                                    <div class="customer-primary">
                                        <span class="customer-name">{{
                                            customer.custNm || customer.displayName || "-"
                                        }}</span>
                                        <span v-if="customer.isDefault" class="customer-default-badge">기본</span>
                                        <button
                                            class="action-button delete"
                                            @click="handleDelete(customer.custNo, customer.custNm)"
                                            :disabled="processingCustNo === customer.custNo"
                                            title="삭제"
                                        >
                                            <i class="material-icons">delete</i>
                                        </button>
                                    </div>
                                    <div class="customer-secondary">
                                        <span class="customer-number">{{ customer.custNo }}</span>
                                        <span v-if="customer.ictg" class="customer-contract">
                                            {{ customer.ictg }}
                                        </span>
                                        <span v-if="customer.ikw" class="customer-power">
                                            {{ customer.ikw.toLocaleString() }}kW
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="customer-actions">
                                <button
                                    class="toggle-switch"
                                    :class="{ active: customer.status === 'active' }"
                                    @click="toggleStatus(customer)"
                                    :disabled="processingCustNo === customer.custNo"
                                    :title="customer.status === 'active' ? '비활성화' : '활성화'"
                                >
                                    <span class="toggle-slider">
                                        <i
                                            v-if="processingCustNo === customer.custNo"
                                            class="material-icons spinner-icon"
                                        >
                                            sync
                                        </i>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 추가 가능한 고객 섹션 -->
            <div class="section section-right">
                <div class="section-header">
                    <div class="section-title-row">
                        <h4 class="section-title">추가 가능한 고객</h4>
                        <span class="count-badge">{{ filteredAvailableCustomers.length }}</span>
                    </div>
                    <div class="search-box">
                        <i class="material-icons search-icon">search</i>
                        <input
                            v-model="searchAvailable"
                            type="text"
                            class="search-input"
                            placeholder="고객명 또는 고객번호 검색..."
                            @input="onSearchAvailable"
                        />
                        <i v-if="searchAvailable" class="material-icons clear-icon" @click="clearSearchAvailable">
                            close
                        </i>
                    </div>
                </div>
                <div class="customer-list-container">
                    <div v-if="isLoadingAvailable" class="loading-state">
                        <i class="material-icons spinner-icon">sync</i>
                        <span>로딩 중...</span>
                    </div>
                    <div v-else-if="availableCustomers.length === 0" class="empty-state">
                        <i class="material-icons empty-icon">check_circle_outline</i>
                        <p class="empty-text">모든 고객이 등록되었습니다</p>
                    </div>
                    <div v-else-if="filteredAvailableCustomers.length === 0" class="empty-state">
                        <i class="material-icons empty-icon">search_off</i>
                        <p class="empty-text">검색 결과가 없습니다</p>
                    </div>
                    <div v-else class="customer-list">
                        <div
                            v-for="customer in filteredAvailableCustomers"
                            :key="customer.custNo"
                            class="customer-item available"
                        >
                            <div class="customer-main">
                                <div class="customer-info">
                                    <div class="customer-primary">
                                        <span class="customer-name">{{ customer.custNm || "-" }}</span>
                                    </div>
                                    <div class="customer-secondary">
                                        <span class="customer-number">{{ customer.custNo }}</span>
                                        <span v-if="customer.ictg" class="customer-contract">{{ customer.ictg }}</span>
                                        <span v-if="customer.ikw" class="customer-power">
                                            {{ customer.ikw.toLocaleString() }}kW
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="customer-actions">
                                <button
                                    class="action-button add"
                                    @click="handleAdd(customer.custNo)"
                                    :disabled="processingCustNo === customer.custNo"
                                    title="추가"
                                >
                                    <i v-if="processingCustNo === customer.custNo" class="material-icons spinner-icon">
                                        sync
                                    </i>
                                    <i v-else class="material-icons">add</i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import "material-icons/iconfont/material-icons.css";

export default {
    name: "CustomerManagementModal",
    props: {
        value: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            registeredCustomers: [],
            availableCustomers: [],
            isLoadingAvailable: false,
            isLoadingRegistered: false,
            processingCustNo: null,
            searchRegistered: "",
            searchAvailable: "",
        };
    },
    computed: {
        isModalOpen: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit("input", val);
            },
        },
        activeCustomerCount() {
            return this.registeredCustomers.filter((c) => c.status === "active").length;
        },
        filteredRegisteredCustomers() {
            if (!this.searchRegistered) return this.registeredCustomers;
            const search = this.searchRegistered.toLowerCase();
            return this.registeredCustomers.filter(
                (customer) =>
                    customer.custNm?.toLowerCase().includes(search) ||
                    customer.displayName?.toLowerCase().includes(search) ||
                    customer.custNo?.toLowerCase().includes(search)
            );
        },
        filteredAvailableCustomers() {
            if (!this.searchAvailable) return this.availableCustomers;
            const search = this.searchAvailable.toLowerCase();
            return this.availableCustomers.filter(
                (customer) =>
                    customer.custNm?.toLowerCase().includes(search) || customer.custNo?.toLowerCase().includes(search)
            );
        },
    },
    watch: {
        value(newValue) {
            if (newValue) {
                this.loadData();
            }
        },
    },
    methods: {
        handleClose() {
            this.isModalOpen = false;
            this.searchRegistered = "";
            this.searchAvailable = "";
        },
        onSearchRegistered() {
            // 검색 시 추가 로직이 필요하면 여기에
        },
        onSearchAvailable() {
            // 검색 시 추가 로직이 필요하면 여기에
        },
        clearSearchRegistered() {
            this.searchRegistered = "";
        },
        clearSearchAvailable() {
            this.searchAvailable = "";
        },
        async loadData() {
            await Promise.all([this.loadRegisteredCustomers(), this.loadAvailableCustomers()]);
        },
        async loadRegisteredCustomers() {
            this.isLoadingRegistered = true;
            try {
                const response = await backEndApi.kepco.getRegisteredCustomers();
                // API 응답 구조: response.data.data.data
                this.registeredCustomers = response.data?.data?.data || [];
            } catch (error) {
                console.error("등록된 고객 목록 조회 실패:", error);
                this.$notify({
                    type: "error",
                    title: "조회 실패",
                    text: "등록된 고객 목록을 불러오는데 실패했습니다.",
                });
            } finally {
                this.isLoadingRegistered = false;
            }
        },
        async loadAvailableCustomers() {
            this.isLoadingAvailable = true;
            try {
                const response = await backEndApi.kepco.getAvailableCustomers();
                // API 응답 구조: response.data.data.customers
                this.availableCustomers = response.data?.data?.customers || [];
            } catch (error) {
                console.error("추가 가능한 고객 목록 조회 실패:", error);
                this.$notify({
                    type: "error",
                    title: "조회 실패",
                    text: "추가 가능한 고객 목록을 불러오는데 실패했습니다.",
                });
            } finally {
                this.isLoadingAvailable = false;
            }
        },
        async handleAdd(custNo) {
            if (this.processingCustNo) return;

            const customer = this.availableCustomers.find((c) => c.custNo === custNo);
            const confirmMsg = `고객 "${customer?.custNm || custNo}"을(를) 추가하시겠습니까?`;

            if (!confirm(confirmMsg)) return;

            this.processingCustNo = custNo;
            try {
                await backEndApi.kepco.addCustomer(custNo);
                this.$notify({
                    type: "success",
                    title: "추가 완료",
                    text: "고객이 성공적으로 추가되었습니다.",
                });
                this.$emit("customer-updated");
                await this.loadData();
            } catch (error) {
                console.error("고객 추가 실패:", error);
                this.$notify({
                    type: "error",
                    title: "추가 실패",
                    text: error.response?.data?.message || "고객 추가에 실패했습니다.",
                });
            } finally {
                this.processingCustNo = null;
            }
        },
        async toggleStatus(customer) {
            if (this.processingCustNo) return;

            const isActive = customer.status === "active";

            this.processingCustNo = customer.custNo;
            try {
                if (isActive) {
                    await backEndApi.kepco.deactivateCustomer(customer.custNo);
                } else {
                    await backEndApi.kepco.activateCustomer(customer.custNo);
                }

                // 로컬 상태 즉시 업데이트
                const index = this.registeredCustomers.findIndex((c) => c.custNo === customer.custNo);
                if (index !== -1) {
                    this.registeredCustomers[index].status = isActive ? "inactive" : "active";
                }

                this.$emit("customer-updated");
            } catch (error) {
                const action = isActive ? "비활성화" : "활성화";
                console.error(`고객 ${action} 실패:`, error);
                this.$notify({
                    type: "error",
                    title: `${action} 실패`,
                    text: error.response?.data?.message || `고객 ${action}에 실패했습니다.`,
                });
                // 실패 시 데이터 다시 로드
                await this.loadData();
            } finally {
                this.processingCustNo = null;
            }
        },
        async handleDelete(custNo, custNm) {
            if (this.processingCustNo) return;

            const confirmMsg = `고객 "${custNm || custNo}"을(를) 삭제하시겠습니까?`;

            if (!confirm(confirmMsg)) return;

            this.processingCustNo = custNo;
            try {
                await backEndApi.kepco.deleteCustomer(custNo);
                this.$notify({
                    type: "success",
                    title: "삭제 완료",
                    text: "고객이 성공적으로 삭제되었습니다.",
                });
                this.$emit("customer-updated");
                await this.loadData();
            } catch (error) {
                console.error("고객 삭제 실패:", error);
                this.$notify({
                    type: "error",
                    title: "삭제 실패",
                    text: error.response?.data?.message || "고객 삭제에 실패했습니다.",
                });
            } finally {
                this.processingCustNo = null;
            }
        },
    },
};
</script>

<style>
/* b-modal 커스텀 스타일 */
.customer-management-modal .modal-dialog {
    max-width: 900px;
}

.customer-management-modal .customer-modal-header {
    border-bottom: 1px solid #e2e8f0;
}

.customer-management-modal .customer-modal-header .modal-title {
    color: #1e293b;
}

.customer-management-modal .customer-modal-body {
    padding: 24px;
    max-height: 65vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.customer-management-modal .customer-modal-body::-webkit-scrollbar {
    display: none;
}
</style>

<style scoped>
button:focus {
    outline: none;
}
.modal-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    flex: 1;
    min-height: 0;
    height: 100%;
}

.section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-height: 0;
}

.section-left {
    padding-right: 16px;
    border-right: 1px solid #e2e8f0;
}

.section-right {
    padding-left: 0;
}

.section-header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.section-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.section-title {
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-summary {
    display: flex;
    align-items: center;
    gap: 4px;
}

.status-count {
    font-size: 13px;
    font-weight: 700;
}

.status-count.active {
    color: #10b981;
}

.status-count.total {
    color: #64748b;
}

.status-separator {
    color: #cbd5e1;
    font-weight: 400;
    margin: 0 2px;
}

.count-badge {
    font-size: 11px;
    font-weight: 700;
    color: #3b82f6;
    background: #eff6ff;
    padding: 2px 7px;
    border-radius: 10px;
    min-width: 20px;
    text-align: center;
    line-height: 1.5;
}

.search-box {
    position: relative;
    display: flex;
    align-items: center;
}

.search-icon {
    position: absolute;
    left: 10px;
    font-size: 18px;
    color: #94a3b8;
    pointer-events: none;
    transition: all 0.2s ease;
}

.search-input {
    width: 100%;
    padding: 7px 32px 7px 34px;
    font-size: 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #ffffff;
    color: #1e293b;
    transition: all 0.2s ease;
}

.search-input::placeholder {
    color: #94a3b8;
}

.search-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.search-input:hover:not(:focus) {
    border-color: #cbd5e1;
}

.clear-icon {
    position: absolute;
    right: 8px;
    font-size: 16px;
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 2px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.clear-icon:hover {
    color: #64748b;
    background: #f1f5f9;
}

.clear-icon:active {
    background: #e2e8f0;
}

.customer-list-container {
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
}

.customer-list-container::-webkit-scrollbar {
    width: 6px;
}

.customer-list-container::-webkit-scrollbar-track {
    background: transparent;
}

.customer-list-container::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.customer-list-container::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;
    gap: 10px;
}

.loading-state .spinner-icon {
    font-size: 24px !important;
    color: #3b82f6;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 250px;
    gap: 12px;
    padding: 24px;
}

.empty-icon {
    font-size: 48px !important;
    color: #cbd5e1;
}

.empty-text {
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    margin: 0;
}

.empty-subtext {
    font-size: 12px;
    color: #94a3b8;
    margin: 0;
    font-weight: 400;
}

.customer-list {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px;
}

.customer-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background: #ffffff;
    border-radius: 6px;
    transition: all 0.2s ease;
    gap: 12px;
    border: 1px solid transparent;
}

.customer-item:hover {
    background: #f8fafc;
    border-color: #e2e8f0;
}

.customer-item.inactive {
    opacity: 0.5;
}

.customer-item.available {
    opacity: 1;
}

.customer-main {
    flex: 1;
    min-width: 0;
}

.customer-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.customer-primary {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
}

.customer-name {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
    transition: color 0.2s ease;
}

.customer-item:hover .customer-name {
    color: #1e293b;
}

.customer-default-badge {
    font-size: 8px;
    font-weight: 700;
    padding: 2px 5px;
    border-radius: 3px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    background: #dbeafe;
    color: #1e40af;
    line-height: 1.4;
}

.customer-secondary {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #64748b;
    flex-wrap: wrap;
}

.customer-number {
    font-weight: 600;
}

.customer-contract {
    font-weight: 500;
}

.customer-contract::before {
    content: "•";
    margin-right: 6px;
    color: #cbd5e1;
}

.customer-power {
    font-weight: 600;
    color: #475569;
}

.customer-power::before {
    content: "•";
    margin-right: 6px;
    color: #cbd5e1;
}

.customer-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.toggle-switch {
    position: relative;
    width: 40px;
    height: 22px;
    background: #cbd5e1;
    border: none;
    border-radius: 11px;
    cursor: pointer;
    transition: all 0.25s ease;
    padding: 0;
    flex-shrink: 0;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.15);
}

.toggle-switch:hover:not(:disabled) {
    background: #94a3b8;
}

.toggle-switch.active {
    background: #10b981;
}

.toggle-switch.active:hover:not(:disabled) {
    background: #059669;
}

.toggle-switch:active:not(:disabled) {
    box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-switch:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.toggle-switch:focus {
    outline: none;
}

.toggle-slider {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    background: #ffffff;
    border-radius: 50%;
    transition: all 0.25s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.toggle-switch.active .toggle-slider {
    transform: translateX(18px);
}

.spinner-icon {
    animation: spin 0.8s linear infinite;
}

.toggle-slider .spinner-icon {
    font-size: 10px !important;
    color: #3b82f6;
}

.action-button .spinner-icon {
    font-size: 18px !important;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.action-button {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    overflow: hidden;
}

.action-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
}

.action-button.add {
    width: 28px;
    height: 28px;
    background: #3b82f6;
    color: #ffffff;
    box-shadow: 0 1px 3px rgba(59, 130, 246, 0.3);
}

.action-button.add:hover:not(:disabled) {
    background: #2563eb;
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.4);
}

.action-button.add:active:not(:disabled) {
    background: #1d4ed8;
    box-shadow: 0 1px 2px rgba(59, 130, 246, 0.3);
}

.action-button.delete {
    width: 20px;
    height: 20px;
    background: transparent;
    color: #cbd5e1;
    border: none;
    opacity: 0;
    transition: all 0.2s ease;
}

.customer-item:hover .action-button.delete {
    opacity: 1;
}

.action-button.delete:hover:not(:disabled) {
    background: #fef2f2;
    color: #ef4444;
}

.action-button.delete:active:not(:disabled) {
    background: #fee2e2;
    color: #dc2626;
}

.action-button .material-icons {
    line-height: 1;
    position: relative;
    z-index: 1;
    transition: all 0.2s ease;
}

.action-button.add .material-icons {
    font-size: 18px;
}

.action-button.delete .material-icons {
    font-size: 19px;
}
</style>
